import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { APP_MODES } from "../utils/appMode";
import { THEME_MODES } from "../utils/themeMode";
import SettingsAppearancePage from "./settings/SettingsAppearancePage";
import SettingsAppModePage from "./settings/SettingsAppModePage";
import SettingsHub from "./settings/SettingsHub";
import SettingsPushNotificationsPage from "./settings/SettingsPushNotificationsPage";
import { SETTINGS_PAGES } from "./settings/settingsPages";
import usePushRegistrationController from "./settings/notification-preferences/usePushRegistrationController";
import "../styles/components/SettingsPanel.css";

export { SETTINGS_PAGES };

function normalizeInitialPage(value) {
  return Object.values(SETTINGS_PAGES).includes(value) ? value : SETTINGS_PAGES.HUB;
}

function getPushStatus(registration) {
  if (!registration.registrationEnabled || !registration.vapidConfigured) {
    return "not-configured";
  }
  return registration.active ? "active" : "disabled";
}

function getFocusable(container) {
  return Array.from(
    container?.querySelectorAll?.(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ) || []
  );
}

function SettingsPanelDialog({
  onClose,
  initialPage,
  appMode,
  onAppModeChange,
  themeMode,
  onThemeModeChange,
  currentUser,
}) {
  const { t } = useTranslation();
  const registration = usePushRegistrationController();
  const normalizedInitialPage = normalizeInitialPage(initialPage);
  const [activePage, setActivePage] = useState(normalizedInitialPage);
  const dialogRef = useRef(null);
  const headingRef = useRef(null);

  useEffect(() => {
    setActivePage(normalizedInitialPage);
  }, [normalizedInitialPage]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    headingRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = getFocusable(dialogRef.current);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus?.();
    };
  }, [onClose]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [activePage]);

  const pageTitles = {
    [SETTINGS_PAGES.HUB]: t("settings", { defaultValue: "Settings" }),
    [SETTINGS_PAGES.APP_MODE]: t("settings_hub_app_mode_title", { defaultValue: "App mode" }),
    [SETTINGS_PAGES.APPEARANCE]: t("settings_appearance", { defaultValue: "Appearance" }),
    [SETTINGS_PAGES.PUSH]: t("settings_hub_push_title", { defaultValue: "Push notifications" }),
  };
  const isHub = activePage === SETTINGS_PAGES.HUB;

  return (
    <div className="rp-settings-panel-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className={`rp-settings-panel rp-settings-panel--${activePage}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rp-settings-panel-title"
      >
        <div className="rp-settings-panel__header">
          <div className="rp-settings-panel__heading">
            {!isHub ? (
              <button
                type="button"
                className="rp-settings-panel__back"
                onClick={() => setActivePage(SETTINGS_PAGES.HUB)}
                aria-label={t("settings_back_to_hub", { defaultValue: "Back to Settings" })}
              >
                <ArrowLeft size={18} aria-hidden="true" />
                <span>{t("settings", { defaultValue: "Settings" })}</span>
              </button>
            ) : null}
            <h2 id="rp-settings-panel-title" ref={headingRef} tabIndex="-1">
              {pageTitles[activePage]}
            </h2>
          </div>
          <button
            type="button"
            className="rp-settings-panel__close"
            onClick={onClose}
            aria-label={t("close_settings", { defaultValue: "Close settings" })}
          >
            {"\u00D7"}
          </button>
        </div>

        {activePage === SETTINGS_PAGES.HUB ? (
          <SettingsHub appMode={appMode} themeMode={themeMode} pushStatus={getPushStatus(registration)} onNavigate={setActivePage} />
        ) : null}
        {activePage === SETTINGS_PAGES.APP_MODE ? (
          <SettingsAppModePage appMode={appMode} onAppModeChange={onAppModeChange} />
        ) : null}
        {activePage === SETTINGS_PAGES.APPEARANCE ? (
          <SettingsAppearancePage themeMode={themeMode} onThemeModeChange={onThemeModeChange} />
        ) : null}
        {activePage === SETTINGS_PAGES.PUSH ? (
          <SettingsPushNotificationsPage currentUser={currentUser} />
        ) : null}
      </section>
    </div>
  );
}

export default function SettingsPanel({
  isOpen,
  onClose,
  initialPage = SETTINGS_PAGES.HUB,
  appMode = APP_MODES.FULL,
  onAppModeChange,
  themeMode = THEME_MODES.LIGHT,
  onThemeModeChange,
  currentUser = null,
}) {
  if (!isOpen) return null;

  return (
    <SettingsPanelDialog
      onClose={onClose}
      initialPage={initialPage}
      appMode={appMode}
      onAppModeChange={onAppModeChange}
      themeMode={themeMode}
      onThemeModeChange={onThemeModeChange}
      currentUser={currentUser}
    />
  );
}
